import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import type { Customer, CustomerOrderSummary } from '@/services';
import { EntityLink, Badge } from '@resource-kit/ui/react';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { formatCurrency, formatDateTimeCompact } from '@lib/utils';
import { paymentOptions, fulfillmentOptions } from '@features/orders';

// PR-37 (US-CUSTOMER-002): consumes the inline `orders[]` preview that
// GET /customers/:id now returns (capped at 50, with `orders_truncated`
// signalling the presence of a longer tail). Keep this component thin —
// the detail page already renders the customer stats; this card only
// renders the recent-orders at a glance with drill-in links to each
// order detail. A cross-resource "filter orders by customer" deep link
// is intentionally NOT rendered here: `ResourcePage` does not yet
// consume URL search params as filter state, so such a link would dump
// the operator on an unfiltered orders list. Until that filter-state
// wiring lands (its own commit — affects every resource), truncation
// is communicated purely via text.

// The link from each row to `/orders/:shopify_order_id` uses the order's
// shopify-side id because `orderResource.idField === 'shopify_id'`.
// That is distinct from the customer's `customer.shopify_id` — keeping
// the identifiers separate in variable names avoids future confusion.

// Decimal-string wire amount → project-standard currency string. Guard
// against non-numeric input before delegating to the shared, i18n
// locale-aware formatter (matches the orders pages; no hardcoded locale).
const formatMoney = (value: string, currency: string) => {
  const num = Number.parseFloat(value);
  if (!Number.isFinite(num)) return value;
  return formatCurrency(num, currency);
};

const statusVariant = (
  status: string | null | undefined,
): 'success' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'secondary';
  const s = status.toLowerCase();
  if (s === 'paid' || s === 'fulfilled') return 'success';
  // `partial` / `partially_paid` render as outline to visually separate
  // from the clear-success case without introducing a `warning` variant
  // the Badge primitive does not model.
  if (s === 'partially_paid' || s === 'partial') return 'outline';
  if (s === 'refunded' || s === 'voided' || s === 'cancelled') return 'destructive';
  return 'secondary';
};

export function CustomerOrdersCard({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const orders: CustomerOrderSummary[] = customer.orders ?? [];
  const truncated = customer.orders_truncated === true;
  // #1405: the `/api/customers` wire has no customer-level `currency`
  // (generated `Customer`), so the former `customer.currency || 'USD'`
  // read always resolved to the fallback. Pin the literal — runtime
  // output is identical.
  const currency = 'USD';

  return (
    <section className="lg:col-span-2">
      <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
        <Receipt className="h-4 w-4 text-muted-foreground" />
        {t('customer.orders.sectionTitle')}
        <span className="text-xs text-muted-foreground">
          {truncated
            ? t('customer.orders.countTruncated', { count: orders.length })
            : t('customer.orders.count', { count: orders.length })}
        </span>
      </h3>

      {orders.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">{t('customer.orders.empty')}</div>
      ) : (
        <div className="divide-y divide-border/50">
          {orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0 flex-1">
                <EntityLink
                  to={`/orders/${o.shopify_order_id}`}
                  className="font-mono text-sm truncate"
                >
                  {o.name || `#${o.order_number}`}
                </EntityLink>
                <div className="text-xs text-muted-foreground">
                  {formatDateTimeCompact(o.created_at)}
                </div>
              </div>
              <div className="text-sm font-medium tabular-nums">
                {formatMoney(o.total_price, currency)}
              </div>
              <div className="flex gap-1.5 min-w-[140px] justify-end">
                <Badge variant={statusVariant(o.financial_status)}>
                  {o.financial_status
                    ? lookupEnumLabelOrWarn(
                        o.financial_status,
                        paymentOptions,
                        'customer order financial status',
                      )
                    : t('ui.na')}
                </Badge>
                {o.fulfillment_status && (
                  <Badge variant={statusVariant(o.fulfillment_status)}>
                    {lookupEnumLabelOrWarn(
                      o.fulfillment_status,
                      fulfillmentOptions,
                      'customer order fulfillment status',
                    )}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {truncated && (
        <p className="mt-3 text-xs text-muted-foreground">{t('customer.orders.truncatedHint')}</p>
      )}
    </section>
  );
}
